import React, { useCallback, useEffect, useState } from "react";
import { fetchAppConfig, putAdminAppConfig } from "./appConfigApi";

/** Fallback if app-config has no modelPresets yet (keep in sync with backend portfolioAiSettings.js). */
const MODEL_PRESETS_FALLBACK = [
  "gpt-5.5",
  "gpt-5.4",
  "gpt-5.4-mini",
  "gpt-5.4-nano",
  "gpt-5",
  "gpt-5-mini",
  "gpt-5-nano",
  "gpt-5.3-chat-latest",
  "gpt-4.1",
  "gpt-4.1-mini",
  "gpt-4.1-nano",
  "gpt-4o",
  "gpt-4o-mini",
  "o4-mini",
  "o3",
  "o3-mini",
  "o3-pro",
];

export default function AiSettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modelPresets, setModelPresets] = useState(MODEL_PRESETS_FALLBACK);
  const [model, setModel] = useState("gpt-5.4-mini");
  const [customModel, setCustomModel] = useState("");
  const [apiBaseUrl, setApiBaseUrl] = useState("https://api.openai.com/v1");
  const [dailyLimitPerUser, setDailyLimitPerUser] = useState(25);
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [apiKeyHint, setApiKeyHint] = useState("");
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [clearApiKey, setClearApiKey] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const cfg = await fetchAppConfig();
      const s = cfg?.aiSettings || {};
      const presets = Array.isArray(s.modelPresets) && s.modelPresets.length
        ? s.modelPresets
        : MODEL_PRESETS_FALLBACK;
      setModelPresets(presets);
      const m = String(s.model || "gpt-5.4-mini").trim();
      if (presets.includes(m)) {
        setModel(m);
        setCustomModel("");
      } else {
        setModel("__custom__");
        setCustomModel(m);
      }
      setApiBaseUrl(String(s.apiBaseUrl || "https://api.openai.com/v1").trim());
      setDailyLimitPerUser(Number(s.dailyLimitPerUser) || 25);
      setApiKeyConfigured(Boolean(s.apiKeyConfigured));
      setApiKeyHint(String(s.apiKeyHint || ""));
      setApiKeyInput("");
      setClearApiKey(false);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not load AI settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolvedModel = model === "__custom__" ? customModel.trim() : model;

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      if (!resolvedModel) {
        setError("Model is required.");
        setSaving(false);
        return;
      }
      const patch = {
        model: resolvedModel,
        apiBaseUrl: apiBaseUrl.trim(),
        dailyLimitPerUser: Number(dailyLimitPerUser),
      };
      if (clearApiKey) {
        patch.apiKey = "";
      } else if (apiKeyInput.trim()) {
        patch.apiKey = apiKeyInput.trim();
      }
      await putAdminAppConfig({ aiSettings: patch });
      setMessage("AI settings saved.");
      setApiKeyInput("");
      setClearApiKey(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not save AI settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="plugins-card plugins-card-wide">
        <p className="plugins-card-desc">Loading AI settings…</p>
      </section>
    );
  }

  return (
    <section className="plugins-card plugins-card-wide">
      <h3 className="plugins-card-name" style={{ marginTop: 0 }}>
        Portfolio AI assistant
      </h3>
      <p className="plugins-card-desc">
        OpenAI-compatible API credentials and model (Chat Completions). Presets follow OpenAI&apos;s
        current lineup (GPT-5.5, GPT-5.4, GPT-5, GPT-4.1, GPT-4o, o-series). Keys stay on the
        server. Access: Administrator, Coordinator, and Manager roles.
      </p>

      <form onSubmit={onSave} style={{ maxWidth: 520 }}>
        <label className="user-profile-settings-label" style={{ display: "block", marginBottom: 12 }}>
          Model
          <select
            className="user-profile-settings-input"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            style={{ display: "block", width: "100%", marginTop: 4 }}
          >
            {modelPresets.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
            <option value="__custom__">Custom…</option>
          </select>
        </label>

        {model === "__custom__" ? (
          <label className="user-profile-settings-label" style={{ display: "block", marginBottom: 12 }}>
            Custom model id
            <input
              className="user-profile-settings-input"
              type="text"
              value={customModel}
              onChange={(e) => setCustomModel(e.target.value)}
              placeholder="provider-model-id"
              style={{ display: "block", width: "100%", marginTop: 4 }}
            />
          </label>
        ) : null}

        <label className="user-profile-settings-label" style={{ display: "block", marginBottom: 12 }}>
          API base URL
          <input
            className="user-profile-settings-input"
            type="url"
            value={apiBaseUrl}
            onChange={(e) => setApiBaseUrl(e.target.value)}
            placeholder="https://api.openai.com/v1"
            style={{ display: "block", width: "100%", marginTop: 4 }}
          />
        </label>

        <label className="user-profile-settings-label" style={{ display: "block", marginBottom: 12 }}>
          API key
          <input
            className="user-profile-settings-input"
            type="password"
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            placeholder={
              apiKeyConfigured
                ? `Leave blank to keep ${apiKeyHint || "current key"}`
                : "sk-…"
            }
            autoComplete="off"
            style={{ display: "block", width: "100%", marginTop: 4 }}
          />
        </label>
        {apiKeyConfigured ? (
          <label className="plugins-toggle" style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={clearApiKey}
              onChange={(e) => setClearApiKey(e.target.checked)}
            />
            <span>Remove stored API key</span>
          </label>
        ) : null}

        <label className="user-profile-settings-label" style={{ display: "block", marginBottom: 16 }}>
          Daily questions per user (UTC)
          <input
            className="user-profile-settings-input"
            type="number"
            min={1}
            max={500}
            value={dailyLimitPerUser}
            onChange={(e) => setDailyLimitPerUser(e.target.value)}
            style={{ display: "block", width: "100%", marginTop: 4 }}
          />
        </label>

        <button type="submit" className="user-profile-settings-primary" disabled={saving}>
          {saving ? "Saving…" : "Save AI settings"}
        </button>
      </form>

      {error ? <p className="email-triggers-muted" style={{ color: "#b91c1c" }}>{error}</p> : null}
      {message ? <p className="email-triggers-muted">{message}</p> : null}
    </section>
  );
}
